<template>
  <div class="app-container">
    <header>
      <router-link to="/" class="logo">
        Watsdat
        <span class="version">Alpha 0.0.1</span>
      </router-link>
      <!-- <div class="header-actions" v-if="store.state.user">
        <router-link class="btn" to="/games/new">New Game</router-link>
      </div> -->
    </header>
    <router-view v-if="store.state.user" />
  </div>
</template>

<script lang="ts" setup>
import { useStore } from 'vuex';
import { StoreParams } from './store/types';

const store = useStore<StoreParams>();
</script>

<style>
:root {
  --window-ratio-fix: 1;
  --blue-50: #f5f9ff;
  --blue-100: #d0e1fd;
  --blue-200: #abc9fb;
  --blue-300: #85b2f9;
  --blue-400: #609af8;
  --blue-500: #3b82f6;
  --blue-600: #326fd1;
  --blue-700: #295bac;
  --blue-800: #204887;
  --blue-900: #183462;
  --green-50: #f4fcf7;
  --green-100: #caf1d8;
  --green-200: #a0e6ba;
  --green-300: #76db9b;
  --green-400: #4cd07d;
  --green-500: #22c55e;
  --green-600: #1da750;
  --green-700: #188a42;
  --green-800: #136c34;
  --green-900: #0e4f26;
  --yellow-50: #fefbf3;
  --yellow-100: #faedc4;
  --yellow-200: #f6de95;
  --yellow-300: #f2d066;
  --yellow-400: #eec137;
  --yellow-500: #eab308;
  --yellow-600: #c79807;
  --yellow-700: #a47d06;
  --yellow-800: #816204;
  --yellow-900: #5e4803;
  --cyan-50: #f3fbfd;
  --cyan-100: #c3edf5;
  --cyan-200: #94e0ed;
  --cyan-300: #65d2e4;
  --cyan-400: #35c4dc;
  --cyan-500: #06b6d4;
  --cyan-600: #059bb4;
  --cyan-700: #047f94;
  --cyan-800: #036475;
  --cyan-900: #024955;
  --pink-50: #fef6fa;
  --pink-100: #fad3e7;
  --pink-200: #f7b0d3;
  --pink-300: #f38ec0;
  --pink-400: #f06bac;
  --pink-500: #ec4899;
  --pink-600: #c93d82;
  --pink-700: #a5326b;
  --pink-800: #822854;
  --pink-900: #5e1d3d;
  --indigo-50: #f7f7fe;
  --indigo-100: #dadafc;
  --indigo-200: #bcbdf9;
  --indigo-300: #9ea0f6;
  --indigo-400: #8183f4;
  --indigo-500: #6366f1;
  --indigo-600: #5457cd;
  --indigo-700: #4547a9;
  --indigo-800: #363885;
  --indigo-900: #282960;
  --teal-50: #f3fbfb;
  --teal-100: #c7eeea;
  --teal-200: #9ae0d9;
  --teal-300: #6dd3c8;
  --teal-400: #41c5b7;
  --teal-500: #14b8a6;
  --teal-600: #119c8d;
  --teal-700: #0e8174;
  --teal-800: #0b655b;
  --teal-900: #084a42;
  --orange-50: #fff8f3;
  --orange-100: #feddc7;
  --orange-200: #fcc39b;
  --orange-300: #fba86f;
  --orange-400: #fa8e42;
  --orange-500: #f97316;
  --orange-600: #d46213;
  --orange-700: #ae510f;
  --orange-800: #893f0c;
  --orange-900: #642e09;
  --bluegray-50: #f7f8f9;
  --bluegray-100: #dadee3;
  --bluegray-200: #bcc3cd;
  --bluegray-300: #9fa9b7;
  --bluegray-400: #818ea1;
  --bluegray-500: #64748b;
  --bluegray-600: #556376;
  --bluegray-700: #465161;
  --bluegray-800: #37404c;
  --bluegray-900: #282e38;
  --purple-50: #fbf7ff;
  --purple-100: #ead6fd;
  --purple-200: #dab6fc;
  --purple-300: #c996fa;
  --purple-400: #b975f9;
  --purple-500: #a855f7;
  --purple-600: #8f48d2;
  --purple-700: #763cad;
  --purple-800: #5c2f88;
  --purple-900: #432263;
  --red-50: #fff5f5;
  --red-100: #ffd0ce;
  --red-200: #ffaca7;
  --red-300: #ff8780;
  --red-400: #ff6259;
  --red-500: #ff3d32;
  --red-600: #d9342b;
  --red-700: #b32b23;
  --red-800: #8c221c;
  --red-900: #661814;
}

* {
  box-sizing: border-box;
  outline: none;
}

html, body {
  margin: 0;
  padding: 0;
  background: #333242;
}

.loader,
.loader:after {
  border-radius: 50%;
  width: 10em;
  height: 10em;
}

.loader {
  font-size: 2px;
  position: relative;
  text-indent: -9999em;
  border-top: 1.1em solid rgba(242, 114, 114, 0.2);
  border-right: 1.1em solid rgba(242, 114, 114, 0.2);
  border-bottom: 1.1em solid rgba(242, 114, 114, 0.2);
  border-left: 1.1em solid #FC0;
  transform: translateZ(0);
  animation: load8 1s infinite linear;
}

@keyframes load8 {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

a {
  cursor: pointer;
  color: inherit;
  text-decoration: none;
}

#app {
  position: relative;
  font-family: all-round-gothic, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #fff;
  min-height: 100vh;
}

button {
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-align: left;
  -webkit-appearance: none;
  appearance: none;
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

label.inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

h1, h2, h3, p {
  margin: 0;
}

.btn {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-shrink: 0;
  background: #F36;
  gap: 8px;
  height: 42px;
  border: none;
  padding: 0 24px;
  border-radius: 24px;
  white-space: nowrap;
  color: #fff;
  text-decoration: none;
  font: 500 16px/1 all-round-gothic;
  cursor: pointer;
}

.btn.small {
  height: 32px;
  padding: 0 16px;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.btn.twitch {
  background: #9146ff;
}

.btn.discord {
  background: #5865f2;
}

.btn.ghost {
  background: transparent;
}

.btn.outlined {
  border: 1px solid #F36;
  background: transparent;
}

.btn.outlined.default {
  border-color: #fff;
}

.btn.outlined.danger {
  border-color: #F27272;
  color: #F27272;
}

.btn[disabled] {
  opacity: 0.5;
}

input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 220px;
  height: 16px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.48);
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #FC0;
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #FC0;
  cursor: pointer;
}

.input-range {
  padding: 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
}

header .logo {
  font-size: 36px;
  font-weight: bold;
  color: #fc0;
  line-height: 1;
}

header .logo .version {
  font-size: 14px;
  color: #fff;
  opacity: 0.5;
}

.prompt {
  position: fixed;
  z-index: 2;
  width: 320px;
  bottom: 0;
  right: 0;
  padding: 16px;
  overflow: hidden;
  pointer-events: none;
}

.prompt-container {
  padding: 16px 24px;
  background: rgba(31, 29, 44, 0.8);
  border-radius: 16px;
  transition: all 0.2s ease-in;
  transform: translateY(calc(100% + 16px));
  opacity: 0;
}

.prompt[open] .prompt-container {
  transform: none;
  opacity: 1;
}

.prompt .prompt-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #F36;
}

.window-container {
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 24px;
  visibility: hidden;
  pointer-events: none;
}

.window {
  position: relative;
  width: 290px;
  border-radius: 16px;
  background: #1F1D2C;
  padding: 16px 18px;
  z-index: 2;
  overflow: hidden;
}

.window-container[open] {
  visibility: visible;
  pointer-events: all;
}

.window-title {
  padding: 12px 32px;
  font-weight: 600;
}

.window-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  min-height: 60px;
}

/* .window-option.separator {
  background: url(./assets/ic-h-line.svg) repeat-x left top/auto 2.5px;
  padding-top: 2px;
} */

.window-option:hover {
  background-color: #2C2B3B;
}

.window-option-content {
  flex-grow: 1;
}

.window-option.subdued .window-option-content {
  color: rgba(255, 255, 255, 0.69);
}

.window-option.subdued .icon {
  color: rgba(255, 255, 255, 0.69);
}

.window-option.danger .window-option-content {
  color: #F27272;
}

.window-option.router-link-active .window-option-content,
.window-option.router-link-active .icon {
  color: #FC0;
}

.window-option-title {
  color: #fff;
  margin-bottom: 8px;
}

.window-option-info {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.69);
}

.router-container {
  min-height: calc(100vh * var(--window-ratio-fix) - 600px);
}

.input {
  height: 42px;
  background: #393652;
  border-radius: 24px;
  width: 100%;
  color: #fff;
  font: inherit;
  border: none;
  padding: 0 16px;
  font-weight: 400;
  -webkit-appearance: none;
  appearance: none;
}

.input::placeholder {
  color: rgba(255, 255, 255, 0.69);
  opacity: 1;
}

.input[disabled] {
  opacity: 0.5;
}

.input.auto {
  width: auto;
  text-align: center;
  font-weight: 600;
}

select.input {
  padding-right: 28px;
  font-weight: 600;
  /* background: #393652 url(./assets/ic-chevron-down.svg) no-repeat 92% center/20px auto; */
}

textarea.input {
  height: 110px;
  resize: none;
  padding: 12px 16px;
}

.icon-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.icon-button.l {
  width: 32px;
  height: 32px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  z-index: 100;
  background: rgba(31, 29, 44, 0.8);
}

.overlay .dialog {
  background: rgba(31, 29, 44, 1);
  border-radius: 16px;
  padding: 64px 48px;
  width: 512px;
  max-width: 100%;
  text-align: center;
}

.overlay .dialog-title {
  position: relative;
  font-size: 24px;
  font-weight: bold;
  color: #FC0;
}

.overlay .dialog .field {
  margin-top: 24px;
}

.overlay .dialog-title::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 46px;
  height: 9px;
  margin: auto;
  /* background: url(./assets/title-art.svg) no-repeat center/contain; */
}

.overlay .dialog-content {
  margin: 32px 0;
}

.overlay .dialog-message {
  margin-top: 24px;
  margin-bottom: -24px;
  color: rgba(255, 255, 255, 0.69);
}

.overlay .dialog-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
</style>
